import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFeedback,
  getFeedbackReport,
  getFeedbackReports,
  patchFeedbackReport,
  uploadFeedbackAttachments,
  type FeedbackReport,
  type NewFeedbackReport,
} from '../api/feedback';

/**
 * Triage list. `params` maps straight to the server's query allowlist.
 *
 * `enabled` exists for callers that live inside a collapsed panel — the
 * admin page mounts the feedback section on every visit, and there is no
 * reason to fetch the list until someone opens it.
 */
export const useFeedbackReports = (params: Record<string, string> = {}, enabled = true) =>
  useQuery({
    queryKey: ['feedback', params],
    queryFn: () => getFeedbackReports(params),
    enabled,
  });

/** Single report — the only read that carries body, context and attachments. */
export const useFeedbackReport = (id: string | null) =>
  useQuery({
    queryKey: ['feedback-report', id],
    queryFn: () => getFeedbackReport(id as string),
    enabled: !!id,
  });

export interface SubmitFeedbackResult {
  report: FeedbackReport;
  /** Files that did not upload. The report itself is already saved. */
  failedAttachments: string[];
}

/**
 * Create a report, then upload its attachments.
 *
 * Ordered, not parallel: attachments hang off a report id, so the report
 * has to exist first. An attachment failure does not fail the mutation —
 * it comes back in `failedAttachments` for the caller to mention.
 */
export const useSubmitFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { report: NewFeedbackReport; files: File[] }): Promise<SubmitFeedbackResult> => {
      const report = await createFeedback(input.report);
      const failedAttachments = await uploadFeedbackAttachments(report.id, input.files);
      return { report, failedAttachments };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
};

export const useUpdateFeedbackReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; changes: Partial<FeedbackReport> }) =>
      patchFeedbackReport(input.id, input.changes),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-report', variables.id] });
    },
  });
};
